import { Container, Graphics, Text } from 'pixi.js';
import type { CelebrationKind, DuckColor } from '@duck/contracts';
import { DUCK_HEX } from './species';
import { isReducedMotion } from './reduced-motion';

const CONFETTI_COLORS = [0xffd23f, 0xff6b6b, 0x4ecdc4, 0x5aa9e6, 0xff9ecb, 0x95e06c];

const LABELS: Record<CelebrationKind, string> = {
  quest_completed: '✨ Quest Complete!',
  reputation_received: '⭐ +Reputation',
  release: '🚀 Release!',
  level_up: '⬆️ Level Up!',
  welcome: '👋 Welcome!',
};

interface Particle {
  gfx: Graphics;
  vx: number;
  vy: number;
}

/**
 * A short-lived celebration burst (confetti + a rising label) anchored at a world position.
 * Self-contained: the engine calls {@link update} each frame and removes it when it returns true.
 * These are pure moments of joy — no data, no metrics (see PHILOSOPHY.md).
 */
export class CelebrationBurst {
  readonly root = new Container();
  private readonly particles: Particle[] = [];
  private readonly label: Text;
  private elapsed = 0;
  private readonly lifeMs = 1400;

  // Deterministic-ish spread without Math.random reliance for the angle base.
  constructor(x: number, y: number, kind: CelebrationKind, text?: string) {
    this.root.x = x;
    this.root.y = y - 24;

    for (let i = 0; i < 18; i++) {
      const color = CONFETTI_COLORS[i % CONFETTI_COLORS.length]!;
      const gfx = new Graphics().rect(-3, -3, 6, 6).fill(color);
      const angle = (i / 18) * Math.PI * 2;
      const speed = 80 + (i % 5) * 30;
      this.particles.push({
        gfx,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 120,
      });
      this.root.addChild(gfx);
    }

    this.label = new Text({
      text: text ? `${LABELS[kind]} ${text}` : LABELS[kind],
      style: {
        fill: 0xffffff,
        fontSize: 16,
        fontWeight: '800',
        fontFamily: 'ui-sans-serif, system-ui, sans-serif',
        stroke: { color: 0x0b1020, width: 4 },
        align: 'center',
      },
    });
    this.label.anchor.set(0.5, 1);
    this.label.y = -28;
    this.root.addChild(this.label);
  }

  /** Advance the effect. Returns true when finished (caller should destroy it). */
  update(dtMs: number): boolean {
    this.elapsed += dtMs;
    const dt = dtMs / 1000;
    const t = this.elapsed / this.lifeMs;

    for (const p of this.particles) {
      p.vy += 320 * dt; // gravity
      p.gfx.x += p.vx * dt;
      p.gfx.y += p.vy * dt;
      p.gfx.rotation += dt * 6;
      p.gfx.alpha = Math.max(0, 1 - t);
    }

    // Label rises and fades.
    this.label.y = -28 - t * 28;
    this.label.alpha = Math.max(0, 1 - t * t);

    if (this.elapsed >= this.lifeMs) {
      this.root.destroy({ children: true });
      return true;
    }
    return false;
  }
}

interface Chunk {
  gfx: Graphics;
  vx: number;
  vy: number;
  spin: number;
}

/**
 * The PIÑATA DEATH BURST (ADR-0052): when a duck's HP hits zero in the fire, it goes POP like a piñata —
 * a festive splatter of its body bits + cartoon "innards" (bright pink/cream guts + feathers + confetti,
 * never blood-realism), a quick flash, and a little "💥 POP!". Same self-contained contract as
 * {@link CelebrationBurst} (the engine calls {@link update} each frame, removes it when it returns true).
 * Pure slapstick — the duck has already respawned server-side; this is just the joke. Pooled-friendly:
 * one Container, ~28 chunk Graphics, no per-frame allocation, ~1.6s then it tears itself down. Honors
 * prefers-reduced-motion (a static splat that fades, no spray/flash-ramp), like the sibling fire effects.
 */
export class DuckBurst {
  readonly root = new Container();
  private readonly chunks: Chunk[] = [];
  private readonly flash: Graphics;
  private readonly label: Text;
  private elapsed = 0;
  private readonly lifeMs = 1600;
  private readonly reduced = isReducedMotion();

  constructor(x: number, y: number, duckColor: DuckColor) {
    this.root.x = x;
    this.root.y = y;
    const body = DUCK_HEX[duckColor] ?? 0xffd23f;
    // body bits + cartoon innards (bright, never gory-realistic) + beak-orange feathers.
    const palette = [body, body, 0xff7aa8, 0xc83a52, 0xf3efe2, 0xff9f1c, 0xffd23f];

    // A bright POP flash (a quick expanding pale star), drawn first so chunks fly over it.
    this.flash = new Graphics().star(0, -10, 8, 30, 14).fill({ color: 0xfff3c4, alpha: 0.95 });
    this.root.addChild(this.flash);

    for (let i = 0; i < 28; i++) {
      const color = palette[i % palette.length]!;
      const blob = i % 3 === 0;
      const gfx = blob
        ? new Graphics().circle(0, 0, 3 + (i % 3)).fill(color) // a round "gut" blob
        : new Graphics().rect(-3, -2, 6, 4).fill(color); // a body/confetti shard
      const angle = (i / 28) * Math.PI * 2 + (i % 2 ? 0.2 : -0.2);
      if (this.reduced) {
        // Reduced motion: a STATIC splat — scatter the bits once around the death spot, no spray/gravity/spin.
        const r = 8 + (i % 5) * 4;
        gfx.x = Math.cos(angle) * r;
        gfx.y = -10 + Math.sin(angle) * r * 0.7;
        this.chunks.push({ gfx, vx: 0, vy: 0, spin: 0 });
      } else {
        const speed = 130 + (i % 6) * 34;
        gfx.y = -10;
        this.chunks.push({
          gfx,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed - 150, // bias upward so it sprays up then rains down
          spin: (i % 2 ? 1 : -1) * (6 + (i % 4) * 3),
        });
      }
      this.root.addChild(gfx);
    }

    this.label = new Text({
      text: '💥 POP!',
      style: {
        fill: 0xffe066,
        fontSize: 18,
        fontWeight: '800',
        fontFamily: 'ui-sans-serif, system-ui, sans-serif',
        stroke: { color: 0x0b1020, width: 4 },
        align: 'center',
      },
    });
    this.label.anchor.set(0.5, 1);
    this.label.y = -34;
    this.root.addChild(this.label);
  }

  /** Advance the burst. Returns true when finished (caller destroys it). */
  update(dtMs: number): boolean {
    this.elapsed += dtMs;
    const t = this.elapsed / this.lifeMs;

    if (this.reduced) {
      // Reduced motion: hold the static splat + label, just fade out; a brief STEADY glow (no scale ramp).
      this.flash.alpha = Math.max(0, 1 - this.elapsed / 200) * 0.5;
      for (const c of this.chunks) c.gfx.alpha = Math.max(0, 1 - t);
      this.label.alpha = Math.max(0, 1 - t * t);
    } else {
      const dt = dtMs / 1000;
      // Flash: snaps big then fades out fast in the first ~220ms.
      const f = Math.max(0, 1 - this.elapsed / 220);
      this.flash.alpha = f * 0.95;
      this.flash.scale.set(1 + (1 - f) * 0.8);

      for (const c of this.chunks) {
        c.vy += 540 * dt; // gravity — guts rain down
        c.gfx.x += c.vx * dt;
        c.gfx.y += c.vy * dt;
        c.gfx.rotation += c.spin * dt;
        c.gfx.alpha = Math.max(0, 1 - t * 1.1);
      }
      this.label.y = -34 - t * 30;
      this.label.alpha = Math.max(0, 1 - t * t);
    }

    if (this.elapsed >= this.lifeMs) {
      this.root.destroy({ children: true });
      return true;
    }
    return false;
  }
}
